import { EMAIL_RE, KEY_FIELDS } from "./fields";

export const toCatalog = (list, nameKey) => {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  list.forEach((item) => {
    if (item == null) return;
    if (typeof item !== "object") {
      const name = String(item).trim();
      if (!name || seen.has(`n:${name.toLowerCase()}`)) return;
      seen.add(`n:${name.toLowerCase()}`);
      out.push({
        key: name,
        name,
        uuid: null,
        id: null,
        location_id: null,
        location_uuid: null,
        location_title: null,
      });
      return;
    }
    const name = String(item[nameKey] || item.name || item.title || "").trim();
    if (!name) return;
    const key = String(item.uuid || item.id || name);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      key,
      name,
      uuid: item.uuid || null,
      id: item.id ?? null,
      location_id: item.location_id ?? item.locationId ?? null,
      location_uuid: item.location_uuid || item.locationUuid || null,
      location_title: item.location_title || item.locationTitle || null,
    });
  });
  return out;
};

export const rolesForLocation = (roles, locationItem) => {
  if (!locationItem?.uuid && !locationItem?.id && !locationItem?.key)
    return roles;
  const locUuid = locationItem.uuid || locationItem.key;
  const locId = locationItem.id;
  const scoped = roles.filter((role) => {
    if (role.location_uuid && locUuid)
      return String(role.location_uuid) === String(locUuid);
    if (role.location_id != null && locId != null)
      return String(role.location_id) === String(locId);
    return !role.location_id && !role.location_uuid;
  });
  return scoped.length ? scoped : roles;
};

/** The role a row falls back to when the file named none. */
export const DEFAULT_ROLE_NAME = "employee";

/**
 * The file said nothing about the role, so fall back to the plain "Employee"
 * role for the home this row sits in. A company without one gets null, and the
 * cell stays flagged as it was before.
 */
export const defaultRoleFor = (cells, roleCatalog, locationCatalog) => {
  if (!roleCatalog?.length) return null;
  const locationItem =
    (locationCatalog || []).find(
      (item) => item.key === cells?.default_location_id?.value,
    ) || null;
  return (
    rolesForLocation(roleCatalog, locationItem).find(
      (role) => String(role.name).trim().toLowerCase() === DEFAULT_ROLE_NAME,
    ) || null
  );
};

const nameList = (people) => {
  if (people.length <= 3) {
    if (people.length <= 1) return people.join("");
    return `${people.slice(0, -1).join(", ")} and ${people[people.length - 1]}`;
  }
  return `${people.slice(0, 3).join(", ")} and ${people.length - 3} more`;
};

/**
 * Who could not be created and why, from the failure rows /api/employee-import
 * returns — each one { Name, Email, Message }. The server's Message is shown
 * word for word: it is the only account of what actually went wrong, and
 * rewording it risks saying something the server did not mean. All this adds is
 * attribution and grouping, so several rows failing the same way read as one
 * line instead of the same sentence repeated.
 */
export const importFailureMessage = (failures) => {
  const rows = (Array.isArray(failures) ? failures : []).filter(Boolean);
  if (!rows.length) return "";

  const byMessage = new Map();
  rows.forEach((row) => {
    const message = String(row?.Message ?? "").trim() || "Could not be created";
    const who = String(row?.Email || row?.Name || "").trim();
    if (!byMessage.has(message)) byMessage.set(message, []);
    if (who) byMessage.get(message).push(who);
  });

  return [...byMessage.entries()]
    .map(([message, people]) => {
      if (!people.length) {
        const count = rows.filter(
          (row) => String(row?.Message ?? "").trim() === message,
        ).length;
        return `${count} employee${count === 1 ? "" : "s"}: ${message}`;
      }
      return `${nameList(people)}: ${message}`;
    })
    .join(" · ");
};

/** Lowercased email a row was sent under, or "" — the key rows are matched on. */
export const rowEmail = (row) =>
  String(row?.cells?.personal_email?.value || "")
    .trim()
    .toLowerCase();

/**
 * The server only reports the rows it could NOT create, so anything sent and
 * not named in that list was created. Those rows have to leave the sheet, or
 * the next Create sends them a second time.
 */
export const splitCreated = (sent, failures) => {
  const failed = new Set(
    (Array.isArray(failures) ? failures : [])
      .map((item) =>
        String(item?.Email || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean),
  );
  const rows = Array.isArray(sent) ? sent : [];
  if (!failed.size) return { created: rows, failed: [] };

  const created = [];
  const stillHere = [];
  rows.forEach((row) => {
    const email = rowEmail(row);
    // No email to match on means we cannot prove it was created — keep it,
    // so a row is never dropped from the sheet on a guess.
    if (email && !failed.has(email)) created.push(row);
    else stillHere.push(row);
  });
  return { created, failed: stillHere };
};

/** What just happened, after a Create that did not fully succeed. */
export const importResultMessage = (createdCount, failures, remainingCount) => {
  const rows = (Array.isArray(failures) ? failures : []).filter(Boolean);
  const parts = [];

  if (createdCount > 0) {
    parts.push(
      `${createdCount} employee${createdCount === 1 ? "" : "s"} created.`,
    );
  }
  if (rows.length) {
    parts.push(
      `${rows.length} not created — ${importFailureMessage(rows)}`.trim(),
    );
  }
  if (!parts.length) parts.push("Nothing was created.");
  if (remainingCount > 0) {
    parts.push(
      `${remainingCount} row${remainingCount === 1 ? "" : "s"} left to fix.`,
    );
  }
  return parts.join(" ");
};

/** Rows that should keep the review sheet open after a Create. */
export const importStillOpen = (rows) =>
  (rows || []).some(
    (row) => !row.removed && row.status !== "duplicate",
  );

export const catalogLabel = (item, items) => {
  if (!item) return "";
  const dup =
    (items || []).filter((other) => other.name === item.name).length > 1;
  if (dup && item.location_title)
    return `${item.name} · ${item.location_title}`;
  return item.name;
};

const rowName = (cells) =>
  [cells?.first_name, cells?.middle_name, cells?.last_name]
    .map((cell) => cell?.value)
    .filter(Boolean)
    .join(" ") || "(no name)";

const ensureRequiredCells = (cells) => {
  const next = { ...(cells || {}) };
  KEY_FIELDS.forEach((key) => {
    const value = String(next[key]?.value || "").trim();
    if (value) return;
    next[key] = {
      value: "",
      original: next[key]?.original || "",
      state: "attention",
      note: next[key]?.note || "required",
    };
  });
  return next;
};

export const rowStatus = (cells, existingEmails) => {
  const [row] = applyRowStatuses([{ cells }], existingEmails);
  return row.status;
};

const DUPLICATE_NOTES = [
  "already in the system",
  "same email as another row in this file",
];

export const applyRowStatuses = (rows, existingEmails) => {
  const existing = new Set(
    (existingEmails || []).map((email) => String(email).toLowerCase()),
  );
  const seenInFile = new Set();
  return (rows || []).map((row) => {
    const cells = ensureRequiredCells(row.cells);
    if (row.removed) {
      return {
        ...row,
        cells,
        removed: true,
        status: "removed",
        name: rowName(cells),
      };
    }

    const emailRaw = String(cells.personal_email?.value || "").trim();
    const email = emailRaw.toLowerCase();
    const used = Boolean(
      email && (existing.has(email) || seenInFile.has(email)),
    );

    if (emailRaw && !EMAIL_RE.test(emailRaw)) {
      cells.personal_email = {
        ...(cells.personal_email || {}),
        value: emailRaw,
        state: "attention",
        note: "not a valid email",
      };
    } else if (used) {
      cells.personal_email = {
        ...(cells.personal_email || {}),
        value: cells.personal_email?.value || emailRaw,
        state: cells.personal_email?.state || "ok",
        note: existing.has(email)
          ? "already in the system"
          : "same email as another row in this file",
      };
    } else if (
      cells.personal_email &&
      DUPLICATE_NOTES.includes(cells.personal_email.note)
    ) {
      cells.personal_email = {
        ...cells.personal_email,
        note: "",
      };
    }

    const hasAttention = Object.values(cells).some(
      (cell) => cell?.state === "attention",
    );
    let status = "ready";
    if (email && EMAIL_RE.test(emailRaw) && used) {
      status = "duplicate";
    } else if (hasAttention) {
      status = "attention";
    }
    if (email && EMAIL_RE.test(emailRaw)) seenInFile.add(email);

    return {
      ...row,
      cells,
      removed: false,
      status,
      name: rowName(cells),
    };
  });
};
